
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AuthModal } from '../components/ui/authModal';
import { axFetch } from '../lib/utils';
import { useIsMobile } from '../hooks/useIsMobile';
// import DownloadSection from '../components/DownloadSection';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  ShieldCheck,
  CheckCircle2,
  ArrowRight,
  Lock,
  Zap,
  Send,
  CheckCircle
} from 'lucide-react';

// --- Components ---

// Reusable hook for scroll animations
const useScrollAnimation = (ref: React.RefObject<HTMLElement | null>, threshold = 0.2) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Once visible, we can unobserve if we only want the animation to trigger once
          // observer.unobserve(entry.target); 
        }
      },
      {
        threshold,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [ref, threshold]);

  return isVisible;
};

// Reusable animated section component
const FadeIn = ({
  children,
  className = "",
  delay = "0",
  direction = "up"
}: {
  children: React.ReactNode;
  className?: string;
  delay?: "0" | "100" | "200" | "300" | "400" | "500";
  direction?: "up" | "none" | "scale";
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useScrollAnimation(ref);

  const directionClass = direction === "up" ? "fade-up" : direction === "scale" ? "scale-up" : "fade-in";
  const delayClass = delay !== "0" ? `delay-${delay}` : "";

  return (
    <div
      ref={ref}
      className={`animate-on-scroll ${directionClass} ${isVisible ? 'is-visible' : ''} ${delayClass} ${className}`}
    >
      {children}
    </div>
  );
};

const BrandLogo = ({ size = "md", className = "" }: { size?: "sm" | "md" | "lg"; className?: string }) => {
  const dims = { sm: "w-8 h-8", md: "w-10 h-10", lg: "w-20 h-20" };
  return (
    <div className={`${dims[size]} relative flex items-center justify-center shrink-0 ${className}`}>
      <img
        src="/Alt-X-Logo-v2.png"
        alt="Alt-X Logo"
        className="w-full h-full object-contain"
      />
    </div>
  );
};

const Button = ({
  children,
  className = "",
  variant = "primary",
  onClick,
  type = "button",
  disabled = false
}: {
  children?: React.ReactNode;
  className?: string;
  variant?: "primary" | "secondary" | "outline" | "ghost";
  onClick?: () => void;
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
}) => {
  const base = "px-5 py-2.5 text-[14px] font-normal smooth-transition inline-flex items-center justify-center gap-2 rounded-full active:scale-[0.97]";
  const variants = {
    primary: "glossy-button text-white disabled:opacity-50 disabled:cursor-not-allowed",
    secondary: "bg-white text-[#1d1d1f] border border-black/[0.08] hover:bg-[#f5f5f7] hover:border-black/[0.12] soft-shadow",
    outline: "bg-white/60 backdrop-blur-sm text-[#1d1d1f] border border-black/[0.08] hover:bg-white hover:border-black/[0.12] soft-shadow",
    ghost: "bg-transparent text-[#6e6e73] hover:text-[#1d1d1f] hover:bg-black/[0.04]"
  };
  return (
    <button
      className={`${base} ${variants[variant]} ${className}`}
      onClick={onClick}
      type={type}
      disabled={disabled}
    >
      {children}
    </button>
  );
};

// Section Heading component (currently unused but kept for potential future use)
// const SectionHeading = ({ children, className = "" }: { children?: React.ReactNode; className?: string }) => (
//   <h2 className={`text-4xl md:text-6xl font-extrabold text-[#111] tracking-tighter leading-[0.95] ${className}`}>
//     {children}
//   </h2>
// );

const Banner = () => (
  <div className="fixed top-0 left-0 right-0 h-11 bg-[#1d1d1f] z-[60] flex items-center justify-center gap-2.5 text-white text-[12px] font-normal tracking-wide border-b border-white/[0.08]">
    <span className="text-[#a1a1a6]">Backed by</span>
    <div className="flex items-center gap-2">
      <img src="/Y_Combinator_logo.svg.png" alt="Y Combinator" className="h-4 w-auto opacity-90" />
      <span className="font-medium">Combinator</span>
    </div>
  </div>
);

const Navbar = ({ onSignIn, onSignUp, onRunClick, isAuthenticated, onSignOut, onDashboard }: any) => {
  const navigate = useNavigate();
  const [os, setOs] = useState<'windows' | 'mac' | 'mobile' | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const userAgent = window.navigator.userAgent;
    // @ts-ignore
    const platform = window.navigator.platform;
    const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
    const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
    const mobilePlatforms = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

    if (mobilePlatforms.test(userAgent)) {
      setOs('mobile');
    } else if (macosPlatforms.indexOf(platform) !== -1 || /Mac/.test(userAgent)) {
      setOs('mac');
    } else if (windowsPlatforms.indexOf(platform) !== -1 || /Win/.test(userAgent)) {
      setOs('windows');
    }
  }, []);

  const getDownloadText = () => {
    if (os === 'mobile') return 'Download for Excel';
    if (os === 'mac') return 'Download for Mac';
    if (os === 'windows') return 'Download for Windows';
    return 'Download Add-in';
  };

  const handleNavClick = (id: string) => {
    setMobileMenuOpen(false);
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <>
      <nav className="fixed top-11 left-0 right-0 h-[52px] ultra-glass border-b border-white/20 z-50">
        <div className="w-full px-4 md:px-6 lg:px-8 h-full flex items-center justify-between">
          <div className="flex items-center gap-2.5 cursor-pointer group" onClick={() => {
            if (window.location.pathname === '/excel' || window.location.pathname === '/new') {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              navigate('/excel');
            }
          }}>
            <div className="bg-white/80 backdrop-blur-sm border border-black/[0.06] p-1.5 rounded-xl shadow-sm">
              <BrandLogo size="sm" />
            </div>
            <span className="font-semibold text-[15px] md:text-[17px] tracking-[-0.01em] text-[#1d1d1f]">Alt-X</span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-7">
            <a href="#pricing" className="text-[14px] font-normal text-[#6e6e73] hover:text-[#1d1d1f] smooth-transition" onClick={(e) => {
              e.preventDefault();
              handleNavClick('pricing');
            }}>Pricing</a>
            <a href="#how-it-works" className="text-[14px] font-normal text-[#6e6e73] hover:text-[#1d1d1f] smooth-transition" onClick={(e) => {
              e.preventDefault();
              handleNavClick('how-it-works');
            }}>Workflow</a>
            <a href="/enterprise" className="text-[14px] font-normal text-[#6e6e73] hover:text-[#1d1d1f] smooth-transition">Enterprise</a>
            <a href="/support" className="text-[14px] font-normal text-[#6e6e73] hover:text-[#1d1d1f] smooth-transition">Support</a>
            <Button variant="primary" className="py-1.5 px-4 h-8 text-[12px] font-normal rounded-full" onClick={onRunClick}>{getDownloadText()}</Button>

            {/* Auth removed - no longer needed */}
            {/* {isAuthenticated && (
              <>
                <div className="h-5 w-px bg-black/[0.1]"></div>
                <Button variant="ghost" className="py-1.5 px-3 h-8 text-[12px] font-normal" onClick={onDashboard}>Dashboard</Button>
                <Button variant="outline" className="py-1.5 px-3 h-8 text-[12px] font-normal rounded-full" onClick={onSignOut}>Sign Out</Button>
              </>
            )} */}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2 text-[#1d1d1f] hover:bg-black/5 rounded-lg smooth-transition"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden" style={{ top: '63px' }}>
          <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute top-0 left-0 right-0 ultra-glass border-b border-white/20 shadow-xl">
            <div className="px-4 py-4 space-y-1">
              <button
                onClick={() => handleNavClick('pricing')}
                className="w-full text-left px-4 py-3 text-[15px] font-medium text-[#1d1d1f] hover:bg-black/5 rounded-xl smooth-transition"
              >
                Pricing
              </button>
              <button
                onClick={() => handleNavClick('how-it-works')}
                className="w-full text-left px-4 py-3 text-[15px] font-medium text-[#1d1d1f] hover:bg-black/5 rounded-xl smooth-transition"
              >
                Workflow
              </button>
              <button
                onClick={() => { setMobileMenuOpen(false); navigate('/enterprise'); }}
                className="w-full text-left px-4 py-3 text-[15px] font-medium text-[#1d1d1f] hover:bg-black/5 rounded-xl smooth-transition"
              >
                Enterprise
              </button>
              <button
                onClick={() => { setMobileMenuOpen(false); navigate('/support'); }}
                className="w-full text-left px-4 py-3 text-[15px] font-medium text-[#1d1d1f] hover:bg-black/5 rounded-xl smooth-transition"
              >
                Support
              </button>
              <div className="pt-3">
                <Button 
                  variant="primary" 
                  className="w-full py-3 text-[15px] font-semibold rounded-xl justify-center" 
                  onClick={() => { setMobileMenuOpen(false); onRunClick(); }}
                >
                  {getDownloadText()}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Interactive text component
const InteractiveText = ({ children }: { children: React.ReactNode }) => {
  const textRef = useRef<HTMLSpanElement>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLSpanElement>) => {
    if (!textRef.current) return;
    const rect = textRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;
    const percent = (x / width) * 100;

    // Calculate position for the gradient center
    // We want the gradient to follow the cursor but with a "watery" lag
    textRef.current.style.backgroundPosition = `${percent}% center`;
  };

  const handleMouseLeave = () => {
    if (textRef.current) {
      textRef.current.style.backgroundPosition = 'center center';
    }
  };

  return (
    <span
      ref={textRef}
      className="interactive-gradient-text cursor-default"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </span>
  );
};

const Hero = ({ onRunClick }: { onRunClick: () => void }) => {
  const [os, setOs] = useState<'windows' | 'mac' | 'mobile' | null>(null);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    const userAgent = window.navigator.userAgent;
    // @ts-ignore
    const platform = window.navigator.platform;
    const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
    const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
    const mobilePlatforms = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

    if (mobilePlatforms.test(userAgent)) {
      setOs('mobile');
    } else if (macosPlatforms.indexOf(platform) !== -1 || /Mac/.test(userAgent)) {
      setOs('mac');
    } else if (windowsPlatforms.indexOf(platform) !== -1 || /Win/.test(userAgent)) {
      setOs('windows');
    }
  }, []);

  const handleMobileEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setSubmitError('Please enter your email');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setSubmitError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Import axFetch dynamically to avoid issues
      const { axFetch } = await import('../lib/utils');
      const response = await axFetch('POST', 'contact-us', {
        email,
        message: 'Mobile user - Download link request',
        name: '',
        company: ''
      });

      if (!response.ok) {
        throw new Error('Failed to send email');
      }

      setSubmitSuccess(true);
      setEmail('');
      
      setTimeout(() => setSubmitSuccess(false), 8000);
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitError('Failed to send. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDownloadText = () => {
    if (os === 'mac') return 'Download for Mac';
    if (os === 'windows') return 'Download for Windows';
    return 'Download for Excel';
  };

  return (
    <section className="relative pt-32 md:pt-48 pb-20 md:pb-32 px-4 md:px-6 overflow-hidden excel-grid">
      <div className="absolute inset-0 -z-10">
        {/* Liquid Glass Blobs - Smaller on mobile */}
        <div className="absolute top-[-10%] left-[15%] w-[300px] md:w-[600px] h-[300px] md:h-[600px] liquid-blob rounded-full"></div>
        <div className="absolute top-[20%] right-[10%] w-[350px] md:w-[700px] h-[350px] md:h-[700px] liquid-blob-2 rounded-full"></div>
        <div className="absolute bottom-[10%] left-[40%] w-[250px] md:w-[500px] h-[250px] md:h-[500px] liquid-blob-3 rounded-full"></div>

        {/* Additional ambient glow */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[80%] h-[400px] md:h-[600px] bg-gradient-to-b from-blue-500/[0.05] via-purple-500/[0.03] to-transparent blur-3xl"></div>
      </div>

      <div className="max-w-[980px] mx-auto text-center relative z-10">
        <h1 className="text-[36px] sm:text-[48px] md:text-[80px] lg:text-[96px] font-bold text-[#1d1d1f] mb-6 md:mb-8 tracking-[-0.04em] leading-[1.05] animate-slide-up" style={{ fontWeight: 600 }}>
          AI Excel Modeling <br className="hidden sm:block" /><InteractiveText>You Can Actually Trust</InteractiveText>
        </h1>

        <p className="text-[16px] md:text-[19px] lg:text-[21px] text-[#6e6e73] mb-10 md:mb-14 max-w-[720px] mx-auto font-normal leading-[1.4] tracking-[-0.01em] animate-slide-up px-2" style={{ animationDelay: '0.1s' }}>
          Built for PE, VC, and real estate investors who need correctness, not best guesses
        </p>

        {/* Desktop CTA */}
        {os !== 'mobile' && (
          <>
            <h2 className="text-[28px] sm:text-[36px] md:text-[48px] font-semibold text-[#1d1d1f] tracking-[-0.02em] mb-6 md:mb-8 animate-slide-up flex items-center justify-center flex-wrap gap-x-3" style={{ fontWeight: 600, animationDelay: '0.15s' }}>
              <span>Open beta live Now</span>
            </h2>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <button
                onClick={onRunClick}
                className="group relative inline-flex items-center gap-3 px-8 md:px-10 py-4 md:py-5 bg-gradient-to-r from-[#0071e3] via-[#00d084] to-[#0071e3] bg-[length:200%_auto] text-white rounded-full text-[17px] md:text-[20px] font-semibold tracking-wide hover:animate-gradient-x transition-all transform hover:scale-105 shadow-xl hover:shadow-2xl liquid-shimmer overflow-hidden"
              >
                <span className="relative z-10">{getDownloadText()}</span>
                <ArrowRight className="w-5 h-5 md:w-6 md:h-6 relative z-10 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </>
        )}

        {/* Mobile Email Capture */}
        {os === 'mobile' && (
          <div className="max-w-md mx-auto animate-slide-up px-2" style={{ animationDelay: '0.15s' }}>
            <h2 className="text-[20px] sm:text-[24px] font-semibold text-[#1d1d1f] tracking-[-0.02em] mb-5" style={{ fontWeight: 600 }}>
              Get the download link via email
            </h2>
            
            {submitSuccess ? (
              <div className="p-6 bg-green-50 border border-green-200 rounded-2xl">
                <div className="flex items-center justify-center mb-2">
                  <CheckCircle className="w-6 h-6 text-green-600 mr-2" />
                  <p className="text-green-800 font-semibold">Email sent!</p>
                </div>
                <p className="text-green-700 text-sm">
                  Check your inbox for the download link.
                </p>
              </div>
            ) : (
              <form onSubmit={handleMobileEmailSubmit} className="space-y-4">
                <div className="relative">
                  <Input
                    type="email"
                    placeholder="you@fund.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full h-14 px-6 text-base bg-white border-2 border-gray-200 rounded-full focus:border-[#0071e3] focus:ring-2 focus:ring-[#0071e3]/20"
                    disabled={isSubmitting}
                  />
                </div>
                
                {submitError && (
                  <p className="text-red-600 text-sm">{submitError}</p>
                )}
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full group relative inline-flex items-center justify-center gap-3 px-10 py-4 bg-gradient-to-r from-[#0071e3] via-[#00d084] to-[#0071e3] bg-[length:200%_auto] text-white rounded-full text-[18px] font-semibold tracking-wide hover:animate-gradient-x transition-all transform hover:scale-105 shadow-xl hover:shadow-2xl liquid-shimmer overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <span className="relative z-10">Send Download Link</span>
                      <Send className="w-5 h-5 relative z-10" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        )}

        {/* <div className="mt-24 relative max-w-6xl mx-auto">
        <div className="bg-white border border-brand-border rounded-2xl mockup-shadow overflow-hidden text-left">
          <div className="h-12 bg-zinc-50 border-b border-brand-border flex items-center px-5 justify-between">
            <div className="flex gap-2">
              <div className="w-3 h-3 rounded-full bg-zinc-200"></div>
              <div className="w-3 h-3 rounded-full bg-zinc-200"></div>
              <div className="w-3 h-3 rounded-full bg-zinc-200"></div>
            </div>
            <div className="text-xs font-medium text-zinc-400 font-mono">LBO_Analysis_vFinal.xlsx</div>
            <div className="w-4"></div> 
          </div>
          
          <div className="flex flex-col md:flex-row min-h-[440px]">
            <div className="w-full md:w-80 bg-zinc-50 border-r border-brand-border p-6 flex flex-col gap-6">
               <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-4">Integrity Report</div>
                  <div className="space-y-4">
                    {[
                      { label: "Circular Dependencies", val: "12", color: "bg-brand-blue" },
                      { label: "Logic Breaks", val: "3", color: "bg-red-500" },
                      { label: "Unused Assumptions", val: "8", color: "bg-brand-green" }
                    ].map((s, i) => (
                      <div key={i} className="group">
                        <div className="flex justify-between items-end mb-1.5">
                          <span className="text-[11px] font-semibold text-zinc-600">{s.label}</span>
                          <span className="text-xs font-bold mono text-zinc-900">{s.val}</span>
                        </div>
                        <div className="h-1 bg-zinc-200 rounded-full overflow-hidden">
                          <div className={`h-full ${s.color}`} style={{ width: i === 0 ? '60%' : i === 1 ? '25%' : '40%' }}></div>
                        </div>
                      </div>
                    ))}
                  </div>
               </div>
               <div className="mt-auto pt-6 border-t border-brand-border">
                  <Button className="w-full h-10 py-0 text-xs shadow-none">Repair Logic</Button>
               </div>
            </div>

            <div className="flex-1 p-8 bg-white relative overflow-hidden">
              <div className="flex items-center gap-2 mb-6 text-zinc-400">
                <Terminal size={14} />
                <span className="text-xs font-mono tracking-tight">Audit Tab: Waterfall_Structure</span>
              </div>
              <div className="space-y-2 font-mono text-sm leading-relaxed">
                <div className="flex gap-6 group">
                  <span className="w-8 text-zinc-300 text-right select-none">124</span>
                  <span className="text-zinc-600">=VLOOKUP(Scenario_Key, Global_Assumptions, 4, FALSE)</span>
                </div>
                <div className="flex gap-6 group">
                  <span className="w-8 text-zinc-300 text-right select-none">125</span>
                  <span className="text-zinc-600">=INDEX(Yield_Curve, MATCH(Rate_Type, Curve_Names, 0), 2)</span>
                </div>
                <div className="flex gap-6 items-center bg-red-50 -mx-8 px-8 py-2 border-y border-red-100/50">
                  <span className="w-8 text-red-200 text-right select-none">126</span>
                  <span className="text-zinc-900 font-semibold">=3.25% * SUM(Equity_Draws!C10:C110) + <span className="text-red-600 font-black">204000</span></span>
                  <div className="ml-auto animate-pulse flex items-center gap-1.5 text-[9px] bg-red-600 text-white px-2 py-1 rounded font-bold uppercase tracking-widest shadow-lg shadow-red-200">
                    <AlertCircle size={10} strokeWidth={3} /> Logic Break
                  </div>
                </div>
                <div className="flex gap-6 group">
                  <span className="w-8 text-zinc-300 text-right select-none">127</span>
                  <span className="text-zinc-600">=IFERROR(G126 / (1 + WACC) ^ (Years - 1), 0)</span>
                </div>
              </div>

              <div className="absolute bottom-8 right-8 w-64 bg-white border border-brand-border rounded-xl soft-shadow p-5 border-l-4 border-l-brand-blue">
                <div className="text-[10px] font-bold text-brand-blue uppercase tracking-widest mb-2">Repair Logic</div>
                <p className="text-[11px] text-zinc-500 leading-normal mb-3">Hard-coded value detected in a calculated range. Replace with <span className="mono text-zinc-900 font-bold">Assumption!F12</span>?</p>
                <div className="flex gap-2">
                  <button className="text-[10px] font-bold bg-brand-blue text-white px-3 py-1.5 rounded-md">Apply</button>
                  <button className="text-[10px] font-bold text-zinc-400 hover:text-zinc-900 px-3 py-1.5">Ignore</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> */}
      </div>
    </section>
  );
};

const Showcase = () => {
  const isMobile = useIsMobile();

  const demos = [
    {
      title: "Upload Documents, Build Complete Models",
      desc: "Drop in an offering memorandum or investment deck. Watch Alt-X extract assumptions, build cash flows, and connect every formula—automatically. Real estate waterfall models, LBO analyses, DCF valuations. No manual setup, no hallucinations.",
      video: "/videos/build_anything_v1.mp4",
      poster: "/images/video-thumbnails/build-anything-poster.jpg",
      badge: "File Processing",
      color: "blue"
    },
    {
      title: "Investment Banking Standards, One Click",
      desc: "Apply institutional-grade formatting instantly. Color-coded inputs, consistent fonts, and professional styling across every sheet. No more manual formatting hours before investor reviews.",
      video: "/videos/formatting_vid_updated.mp4",
      poster: "/images/video-thumbnails/formatting-poster.jpg",
      badge: "Auto-Format",
      color: "purple"
    },
    {
      title: "Never Ship a Broken Model Again",
      desc: "Catch every error before your IC sees it. Circular references, hard-coded values, broken links—identified and fixed in seconds. What takes associates 3 hours now takes 30 seconds.",
      video: "/videos/model_audit.mp4",
      poster: "/images/video-thumbnails/audit-poster.jpg",
      badge: "Model Audit",
      color: "red"
    }
  ];

  const getBadgeColor = (color: string) => {
    const colors = {
      blue: "bg-blue-500/10 text-blue-600 border-blue-500/20",
      green: "bg-green-500/10 text-green-600 border-green-500/20",
      purple: "bg-purple-500/10 text-purple-600 border-purple-500/20",
      red: "bg-red-500/10 text-red-600 border-red-500/20"
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  return (
    <section className="py-20 md:py-40 bg-white excel-grid">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 relative z-10">
        <div className="text-center mb-16 md:mb-24">
          <FadeIn direction="scale">
            <div className="inline-flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 rounded-full liquid-glass-badge text-[#0071e3] text-[11px] md:text-[13px] font-semibold tracking-wide mb-6 md:mb-8">
              Product Tour
            </div>
          </FadeIn>
          <FadeIn delay="100">
            <h2 className="text-[32px] sm:text-[40px] md:text-[64px] font-semibold text-[#1d1d1f] tracking-[-0.02em] leading-[1.1] px-4" style={{ fontWeight: 600 }}>See it in action</h2>
          </FadeIn>
        </div>

        <div className="space-y-24 md:space-y-32">
          {demos.map((demo, i) => (
            <div key={i} className={`flex flex-col lg:flex-row items-center gap-10 md:gap-16 ${i % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              <div className="w-full lg:w-[40%] space-y-4 md:space-y-6">
                <FadeIn delay="200" direction="up">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="inline-flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-xl liquid-glass-badge text-[#0071e3] font-bold text-[14px] md:text-[16px]">
                      {i + 1}
                    </div>
                    <div className={`inline-flex items-center px-3 md:px-4 py-1.5 md:py-2 rounded-full border text-[11px] md:text-[12px] font-semibold tracking-wide ${getBadgeColor(demo.color)}`}>
                      {demo.badge}
                    </div>
                  </div>
                  <h3 className="text-[26px] sm:text-[32px] md:text-[44px] font-semibold text-[#1d1d1f] tracking-[-0.02em] leading-[1.1]" style={{ fontWeight: 600 }}>{demo.title}</h3>
                  <p className="text-[15px] md:text-[17px] lg:text-[19px] text-[#6e6e73] font-normal leading-[1.5] tracking-[-0.005em]">
                    {demo.desc}
                  </p>
                </FadeIn>
              </div>
              <div className="w-full lg:w-[60%]">
                <FadeIn delay="300" direction="scale">
                  {/* Browser/App Frame */}
                  <div className="relative group">
                    {/* Glow effect */}
                    <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-blue-500/20 rounded-[40px] blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    
                    {/* Main container with browser chrome */}
                    <div className="relative bg-white rounded-[24px] md:rounded-[32px] shadow-2xl overflow-hidden border border-black/5">
                      {/* Browser chrome */}
                      <div className="h-8 md:h-10 bg-[#f6f6f6] border-b border-black/5 flex items-center px-3 md:px-4 gap-1.5 md:gap-2">
                        <div className="flex gap-1.5 md:gap-2">
                          <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-[#ff5f57]"></div>
                          <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-[#ffbd2e]"></div>
                          <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-[#28ca42]"></div>
                        </div>
                        <div className="flex-1 mx-2 md:mx-4 h-5 md:h-6 bg-white rounded-md border border-black/5 flex items-center px-2 md:px-3">
                          <div className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-green-500 mr-1.5 md:mr-2"></div>
                          <span className="text-[9px] md:text-[10px] text-zinc-400 font-medium">Alt-X Excel Add-in</span>
                        </div>
                      </div>
                      
                      {/* Video container */}
                      <div className="relative aspect-video bg-gradient-to-br from-zinc-50 to-zinc-100">
                        <video
                          src={demo.video}
                          poster={demo.poster}
                          className="absolute inset-0 w-full h-full object-cover"
                          autoPlay={!isMobile}
                          muted
                          loop
                          playsInline
                          preload={isMobile ? "metadata" : "auto"}
                          controls={isMobile}
                          onError={(e) => {
                            console.error('Video failed to load:', demo.video, e);
                          }}
                        />
                        
                        {/* Play button overlay for mobile */}
                        {isMobile && (
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-xl">
                              <svg className="w-6 h-6 md:w-8 md:h-8 text-[#0071e3] ml-1" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z"/>
                              </svg>
                            </div>
                          </div>
                        )}
                        
                        {/* Subtle overlay gradient */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/5 via-transparent to-white/5 pointer-events-none"></div>
                      </div>
                    </div>
                    
                    {/* Floating accent elements */}
                    <div className="absolute -bottom-4 -right-4 w-24 h-24 md:w-32 md:h-32 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full blur-3xl opacity-60 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="absolute -top-4 -left-4 w-20 h-20 md:w-28 md:h-28 bg-gradient-to-br from-green-500/10 to-blue-500/10 rounded-full blur-3xl opacity-50 group-hover:opacity-100 transition-opacity duration-500"></div>
                  </div>
                </FadeIn>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// const Features = () => (
//   <section id="features" className="py-32 bg-zinc-50/50 border-y border-brand-border">
//     <div className="max-w-7xl mx-auto px-6">
//       <div className="flex flex-col md:flex-row md:items-end justify-between mb-24 gap-8">
//         <div className="max-w-xl">
//           <div className="text-brand-blue text-[11px] font-bold uppercase tracking-[0.3em] mb-4">Core Engine</div>
//           <h2 className="text-4xl md:text-6xl font-extrabold text-[#111] tracking-tighter leading-[0.95]">Audit with <br /> structural precision.</h2>
//         </div>
//         <p className="text-zinc-500 text-lg font-medium max-w-sm leading-relaxed">
//           Alt-X isn't just a checker. It's a deterministic analysis layer that indexes every dependency in your workbook.
//         </p>
//       </div>

//       <div className="grid md:grid-cols-3 gap-12">
//         {[
//           {
//             title: "Structural Auditing",
//             desc: "Identifies hard-coded values embedded in formulas and logic that diverges across sheets.",
//             icon: <Search className="text-brand-blue" size={24} />
//           },
//           {
//             title: "Dependency Mapping",
//             desc: "Visualizes circular references, broken external links, and #REF errors instantly.",
//             icon: <Activity className="text-brand-green" size={24} />
//           },
//           {
//             title: "Cross-Tab Reconciliation",
//             desc: "Ensures exit multiples and interest rate floors are consistent from assumptions to summary.",
//             icon: <Code className="text-zinc-400" size={24} />
//           }
//         ].map((f, i) => (
//           <div key={i} className="group p-2 transition-transform duration-500 hover:-translate-y-1">
//             <div className="w-14 h-14 bg-white border border-brand-border rounded-2xl soft-shadow flex items-center justify-center mb-8 group-hover:scale-110 transition-transform">
//               {f.icon}
//             </div>
//             <h3 className="text-xl font-bold text-zinc-900 mb-4">{f.title}</h3>
//             <p className="text-zinc-500 text-sm leading-relaxed tracking-tight font-medium">{f.desc}</p>
//           </div>
//         ))}
//       </div>
//     </div>
//   </section>
// );

// const UseCases = () => (
//   <section id="use-cases" className="py-32 bg-white overflow-hidden">
//     <div className="max-w-7xl mx-auto px-6">
//       <div className="text-center mb-20">
//         <div className="text-brand-green text-[11px] font-bold uppercase tracking-[0.3em] mb-4">Deal Scenarios</div>
//         <h2 className="text-4xl md:text-6xl font-extrabold text-[#111] tracking-tighter">Finance Native Logic</h2>
//       </div>

//       <div className="grid md:grid-cols-2 gap-8">
//         {[
//           {
//             title: "LBO Pre-IC Audit",
//             problem: "Reviewing a 100-tab LBO. A single hard-code in the debt schedule is suppressing leverage impact.",
//             solution: "Alt-X instantly flags anomalies across the entire cap table and traces every entry-to-exit dependency.",
//             icon: <BarChart3 className="text-brand-blue" />
//           },
//           {
//             title: "RE Waterfall Integrity",
//             problem: "RE waterfall breaks during sensitivity analysis due to a hidden circularity in the catch-up layer.",
//             solution: "Maps the entire cash flow dependency tree. Identifies where the circularity starts and suggests a fix.",
//             icon: <Layers className="text-brand-green" />
//           }
//         ].map((u, i) => (
//           <div key={i} className="flex flex-col bg-zinc-50 border border-brand-border rounded-3xl overflow-hidden group">
//             <div className="p-10 border-b border-brand-border">
//               <div className="flex items-center gap-4 mb-6">
//                 <div className="w-12 h-12 bg-white soft-shadow rounded-xl flex items-center justify-center border border-brand-border">{u.icon}</div>
//                 <h3 className="font-bold text-2xl text-zinc-900 tracking-tight">{u.title}</h3>
//               </div>
//               <p className="text-zinc-500 text-sm font-medium leading-relaxed italic">"{u.problem}"</p>
//             </div>
//             <div className="p-10 bg-white">
//               <div className="text-[10px] font-bold text-brand-blue uppercase tracking-[0.2em] mb-4">Alt-X Resolution</div>
//               <p className="text-zinc-900 text-base font-semibold leading-relaxed">{u.solution}</p>
//             </div>
//           </div>
//         ))}
//       </div>
//     </div>
//   </section>
// );

const Pricing = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  return (
    <section id="pricing" className="py-20 md:py-40 bg-[#fbfbfd] excel-grid">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 relative z-10">
        <div className="text-center mb-12 md:mb-20">
          <FadeIn direction="scale">
            <div className="inline-flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 rounded-full liquid-glass-badge text-[#0071e3] text-[11px] md:text-[13px] font-semibold tracking-wide mb-6 md:mb-8">
              Pricing
            </div>
          </FadeIn>
          <FadeIn delay="100">
            <h2 className="text-[32px] sm:text-[40px] md:text-[64px] font-semibold text-[#1d1d1f] tracking-[-0.02em] leading-[1.1] px-4" style={{ fontWeight: 600 }}>Start shipping faster today</h2>
          </FadeIn>
        </div>

        <div className={`grid ${isMobile ? 'grid-cols-1' : 'md:grid-cols-4'} gap-4 md:gap-6`}>
          {[
            {
              name: "Free Trial",
              price: "$0",
              period: "/7 days",
              desc: "Full access to Pro features for one week.",
              features: ["Full Audit Suite", "1 User", "Basic Support"],
              cta: "Start Free Trial",
              variant: "default",
              className: "bg-zinc-900 hover:bg-zinc-800 text-white",
              href: "/download"
            },
            {
              name: "Pro",
              price: "$30",
              period: "/mo",
              desc: "For individual analysts and associates.",
              features: ["Unlimited Audits", "Dependency Mapping", "Email Support"],
              cta: "Get Pro",
              variant: "primary",
              highlight: true,
              href: "/download"
            },
            {
              name: "Enterprise",
              price: "$60",
              period: "/mo",
              desc: "For deal teams requiring collaboration.",
              features: ["Team Management", "SSO Integration", "Priority Support"],
              cta: "Contact Sales",
              variant: "outline",
              href: "mailto:team@alt-x.co?subject=Enterprise%20Plan%20Inquiry"
            },
            {
              name: "Ultra",
              price: "$300",
              period: "/mo",
              desc: "Custom deployments and dedicated account manager.",
              features: ["On-Premise Option", "Custom Rules Engine", "24/7 Phone Support"],
              cta: "Contact Sales",
              variant: "outline",
              href: "mailto:team@alt-x.co"
            }
          ].map((plan, i) => (
            <FadeIn key={i} delay={i === 0 ? "200" : i === 1 ? "300" : i === 2 ? "400" : "500"} className="h-full">
              <div className={`flex flex-col p-9 rounded-[32px] border h-full ${plan.highlight
                ? 'ultra-glass liquid-border liquid-glow relative scale-[1.02]'
                : 'liquid-glass border-white/20'
                }`}>
                {plan.highlight && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 liquid-glass-badge text-[#0071e3] text-[12px] font-bold tracking-wider px-6 py-2.5 rounded-full z-10 whitespace-nowrap flex items-center justify-center">
                    Most Popular
                  </div>
                )}
                <h3 className="text-[17px] font-semibold text-[#1d1d1f] mb-2">{plan.name}</h3>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-[48px] font-semibold text-[#1d1d1f] tracking-tight" style={{ fontWeight: 600 }}>{plan.price}</span>
                  <span className="text-[15px] font-normal text-[#6e6e73]">{plan.period}</span>
                </div>
                <p className="text-[14px] text-[#6e6e73] mb-8 leading-relaxed h-12 font-normal">{plan.desc}</p>

                <div className="space-y-3 mb-8 flex-1">
                  {plan.features.map((f, j) => (
                    <div key={j} className="flex items-center gap-2.5 text-[14px] text-[#1d1d1f] font-normal">
                      <CheckCircle2 size={18} className="text-[#0071e3] shrink-0" strokeWidth={2} />
                      <span>{f}</span>
                    </div>
                  ))}
                </div>

                <Button
                  variant={plan.variant as any}
                  className={`w-full justify-center ${plan.className || ''}`}
                  onClick={() => {
                    if (plan.href) {
                      if (plan.href.startsWith('http') || plan.href.startsWith('mailto')) {
                        window.location.href = plan.href;
                      } else {
                        navigate(plan.href);
                      }
                    }
                  }}
                >
                  {plan.cta}
                </Button>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
};

const HowItWorks = () => (
  <section id="how-it-works" className="py-20 md:py-40 bg-white excel-grid">
    <div className="max-w-[1200px] mx-auto px-4 md:px-6 relative z-10">
      <div className="text-center mb-16 md:mb-28">
        <FadeIn direction="scale">
          <div className="inline-flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 rounded-full liquid-glass-badge text-[#6e6e73] text-[11px] md:text-[13px] font-semibold tracking-wide mb-6 md:mb-8">
            The Workflow
          </div>
        </FadeIn>
        <FadeIn delay="100">
          <h2 className="text-[32px] sm:text-[40px] md:text-[64px] font-semibold text-[#1d1d1f] tracking-[-0.02em] leading-[1.1] px-4" style={{ fontWeight: 600 }}>Live in Excel. No migration required.</h2>
        </FadeIn>
      </div>

      <div className="grid md:grid-cols-3 gap-10 md:gap-16">
        {[
          { step: "01", title: "One-Click Install", desc: "Lives in your Excel ribbon. Zero IT overhead. Works with every model you already have." },
          { step: "02", title: "Instant Model Intelligence", desc: "Analyzes your entire workbook in seconds. Every formula, dependency, and risk—mapped automatically." },
          { step: "03", title: "Ship With Confidence", desc: "Fix errors instantly. Lock your logic. Export audit-ready reports that satisfy partners and LPs." }
        ].map((s, i) => (
          <FadeIn key={i} delay={i === 0 ? "200" : i === 1 ? "300" : "400"} direction="up">
            <div className="relative flex flex-col items-center text-center">
              <div className="text-[48px] md:text-[80px] font-semibold mb-6 md:mb-8 leading-none select-none tracking-tighter" style={{ fontWeight: 700 }}>
                <InteractiveText>{s.step}</InteractiveText>
              </div>
              <h3 className="text-[20px] md:text-[24px] font-bold text-[#1d1d1f] mb-3 md:mb-4 tracking-[-0.01em]">{s.title}</h3>
              <p className="text-[15px] md:text-[17px] text-[#1d1d1f] font-medium leading-[1.5] max-w-xs">{s.desc}</p>
            </div>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

const Proof = () => (
  <section className="py-20 bg-white excel-grid border-b border-zinc-100">
    <div className="max-w-[1200px] mx-auto px-6 relative z-10">
      <div className="text-center mb-12">
        <FadeIn>
          <p className="text-[17px] font-semibold text-[#86868b] tracking-wide">Trusted by investors at</p>
        </FadeIn>
      </div>
      <div className="flex flex-wrap justify-center gap-16 md:gap-20 items-center mb-20">
        {['leonardgreen', 'Stanford', 'yukon', 'vga', 'weinstock'].map((client, i) => (
          <FadeIn key={client} delay={i === 0 ? "100" : i === 1 ? "200" : i === 2 ? "300" : i === 3 ? "400" : "500"}>
            <img
              src={`/images/client-logos/${client}.png`}
              alt={`${client} logo`}
              className={`h-10 md:h-14 w-auto object-contain ${client === 'vga' ? 'opacity-25 brightness-0 mix-blend-multiply' : 'opacity-25 grayscale mix-blend-multiply'}`}
            />
          </FadeIn>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {[
          {
            quote: "Alt-X made us question why we ever paid for ARGUS. Everything we needed—cash flow modeling, audits, stress testing—now happens directly in Excel, faster and with more control. This is the first tool that actually feels built for how real estate investors work.",
            author: "Principal",
            fund: "Real Estate PE Fund"
          },
          {
            quote: "Alt-X changed how we build and review LBOs. The model is no longer the bottleneck, it's something we interact with naturally. It's the first tool of its kind that we can use and trust with analyst level tasks, and move quickly without worrying about it breaking.",
            author: "Associate, Buyout",
            fund: "UMM Private Equity Fund"
          }
        ].map((t, i) => (
          <FadeIn key={i} delay={i === 0 ? "200" : "400"} direction="up">
            <div className="p-12 ultra-glass rounded-[36px] relative overflow-hidden">
              <p className="text-[21px] font-normal mb-12 leading-[1.4] tracking-[-0.01em] text-[#1d1d1f]">"{t.quote}"</p>
              <div>
                <div className="font-semibold text-[15px] text-[#1d1d1f] mb-1">{t.author}</div>
                <div className="text-[13px] text-[#86868b] font-normal tracking-wide">{t.fund}</div>
              </div>
              <div className="absolute top-0 right-0 p-10 opacity-[0.03]">
                <div className="bg-black/5 p-2 rounded-xl">
                  <BrandLogo size="lg" />
                </div>
              </div>
            </div>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

const Security = () => (
  <section className="py-20 md:py-40 bg-white excel-grid">
    <div className="max-w-[980px] mx-auto px-4 md:px-6 text-center relative z-10">
      <FadeIn direction="scale">
        <div className="inline-flex items-center justify-center w-16 h-16 md:w-24 md:h-24 liquid-glass-badge text-[#0071e3] rounded-[20px] md:rounded-[28px] mb-8 md:mb-12">
          <ShieldCheck size={32} strokeWidth={1.5} className="md:w-10 md:h-10" />
        </div>
      </FadeIn>
      <FadeIn delay="100">
        <h2 className="text-[32px] sm:text-[40px] md:text-[56px] font-semibold text-[#1d1d1f] mb-4 md:mb-6 tracking-[-0.02em] leading-[1.1] px-4" style={{ fontWeight: 600 }}>Bank-grade security. Zero data risk.</h2>
        <p className="text-[16px] md:text-[19px] text-[#6e6e73] mb-16 md:mb-24 font-normal leading-[1.5] px-4">100% local processing. Your models never leave your machine.</p>
      </FadeIn>

      <div className="grid md:grid-cols-3 gap-6 text-left">
        {[
          { title: "100% Local Processing", desc: "Every calculation runs on your machine. Your models stay on your machine. Period.", icon: <Lock size={20} strokeWidth={1.5} /> },
          { title: "Zero Cloud Storage", desc: "We never store, cache, or transmit your formulas, assumptions, or deal data. Ever.", icon: <CheckCircle2 size={20} strokeWidth={1.5} /> },
          { title: "Enterprise Security Standard", desc: "Built for bulge bracket banks. SOC2 compliance and on-premise deployment available.", icon: <Zap size={20} strokeWidth={1.5} />, pending: true }
        ].map((s, i) => (
          <FadeIn key={i} delay={i === 0 ? "200" : i === 1 ? "300" : "400"} direction="up" className="h-full">
            <div className="p-10 liquid-glass rounded-[32px] relative border border-white/20 h-full">
              <div className="flex justify-between items-start mb-8">
                <div className="text-[#0071e3]">{s.icon}</div>
                {s.pending && (
                  <span className="liquid-glass-badge text-[#0071e3] text-[11px] font-bold px-5 py-2 rounded-full tracking-wider">
                    Pending
                  </span>
                )}
              </div>
              <h4 className="font-semibold text-[#1d1d1f] text-[15px] mb-3 tracking-[-0.01em]">{s.title}</h4>
              <p className="text-[#6e6e73] text-[14px] leading-[1.5] font-normal">{s.desc}</p>
            </div>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);


const Footer = () => (
  <footer className="py-16 bg-[#f5f5f7] border-t border-black/[0.06]">
    <div className="max-w-[980px] mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-12">
      <div className="flex items-center gap-2.5">
        <div className="bg-white border border-black/[0.06] p-1 rounded-lg shadow-sm">
          <BrandLogo size="sm" />
        </div>
        <span className="font-semibold text-[15px] tracking-[-0.01em] text-[#1d1d1f]">Alt-X Financial</span>
      </div>
      <div className="text-[#86868b] text-[12px] font-normal tracking-normal select-none">
        © {new Date().getFullYear()} Alt-X Financial
      </div>
      <div className="flex gap-8 text-[12px] font-normal text-[#6e6e73]">
        <a href="/privacy-policy" className="hover:text-[#1d1d1f] transition-colors">Privacy</a>
        <a href="/terms-of-service" className="hover:text-[#1d1d1f] transition-colors">Terms</a>
        <a href="/eula" className="hover:text-[#1d1d1f] transition-colors">EULA</a>
      </div>
    </div>
  </footer>
);

export default function LandingPageNew() {
  const navigate = useNavigate();
  // const { isAuthenticated, logout } = useAuth(); // Auth removed - no longer needed

  const runSectionRef = useRef<HTMLElement>(null);
  // const [showSignInModal, setShowSignInModal] = useState(false); // Auth modal removed
  // const modalMode = 'signin';

  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    description: ''
  });
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState('');

  // Handle Auth Modal Close from Event (if dispatched globally)
  // useEffect(() => {
  //   const handleCloseAuthModal = () => {
  //     setShowSignInModal(false);
  //   };

  //   window.addEventListener('closeAuthModal', handleCloseAuthModal);
  //   return () => {
  //     window.removeEventListener('closeAuthModal', handleCloseAuthModal);
  //   };
  // }, []);

  const handleContactInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!contactForm.email.trim() || !contactForm.description.trim()) {
      setContactError('Please fill in email and message');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(contactForm.email)) {
      setContactError('Please enter a valid email address');
      return;
    }

    setIsSubmittingContact(true);
    setContactError('');

    try {
      const response = await axFetch('POST', 'contact-us', {
        name: contactForm.name,
        email: contactForm.email,
        company: contactForm.company,
        message: contactForm.description
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send message');
      }

      setContactSuccess(true);
      setContactForm({
        name: '',
        email: '',
        company: '',
        description: ''
      });

      // Reset success message after 8 seconds
      setTimeout(() => {
        setContactSuccess(false);
      }, 8000);

    } catch (error) {
      console.error('Error sending contact message:', error);
      setContactError(error instanceof Error ? error.message : 'Failed to send message. Please try again.');
    } finally {
      setIsSubmittingContact(false);
    }
  };

  const handleHeroCTA = () => {
    navigate('/download');
  };

  // const handleSignOut = () => {
  //   logout();
  // };

  // const handleDashboard = () => {
  //   navigate('/dashboard');
  // };

  return (
    <div className="min-h-screen bg-white text-[#1d1d1f] selection:bg-[#0071e3]/10 selection:text-[#0071e3] antialiased" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
      <Banner />
      <Navbar
        onRunClick={handleHeroCTA}
        isAuthenticated={false}
        onSignOut={() => {}}
        onDashboard={() => {}}
      />

      {/* Auth Modal - Removed, no longer needed */}
      {/* <AuthModal
        showModal={showSignInModal}
        onClose={() => setShowSignInModal(false)}
        initialMode={modalMode}
      /> */}

      <main>
        <Hero onRunClick={handleHeroCTA} />
        <Proof />
        <Showcase />
        {/* <Features /> */}
        {/* <UseCases /> */}
        <Pricing />
        <HowItWorks />
        <Security />
        {/* <DownloadSection id="download" /> */}

        {/* Final CTA / Contact Form Section */}
        <section ref={runSectionRef} id="contact" className="py-40 bg-white border-t border-zinc-200/50 relative overflow-hidden excel-grid">
          {/* Liquid Glass Background Blobs */}
          <div className="absolute top-[10%] left-[5%] w-[600px] h-[600px] liquid-blob rounded-full -z-10"></div>
          <div className="absolute bottom-[5%] right-[10%] w-[700px] h-[700px] liquid-blob-2 rounded-full -z-10"></div>

          <div className="max-w-4xl mx-auto px-6 text-center relative z-20">
            <FadeIn direction="up">
              <h2 className="text-[48px] md:text-[64px] font-semibold text-[#1d1d1f] mb-6 tracking-[-0.02em] leading-[1.1]" style={{ fontWeight: 600 }}>
                Ready to transform <br /> <span className="text-[#86868b]">your modeling workflow?</span>
              </h2>
              <p className="text-[19px] text-[#6e6e73] mb-12 max-w-2xl mx-auto font-normal leading-[1.5]">
                Enterprise deployments, custom integrations, or any questions—we're here to help.
              </p>
            </FadeIn>

            {/* Contact Form */}
            <FadeIn delay="200" direction="scale">
              <div className="ultra-glass rounded-[40px] p-12 border-2 border-white/30 max-w-2xl mx-auto text-left">
                <div className="text-center mb-10">
                  <h3 className="text-[28px] font-semibold text-[#1d1d1f] tracking-[-0.01em]">Contact Us</h3>
                  <p className="text-[#6e6e73] text-[15px] mt-3 font-normal">Fill out the form below and we'll get back to you shortly.</p>
                </div>
                {contactSuccess && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center mb-2">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                      <p className="text-green-800 font-semibold">Message sent successfully!</p>
                    </div>
                    <p className="text-green-700 text-sm ml-7">
                      We'll email you within 1 business day with your audit report.
                    </p>
                  </div>
                )}

                {contactError && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-800">{contactError}</p>
                  </div>
                )}

                <form onSubmit={handleContactSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-zinc-900 mb-2">
                        Name
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={contactForm.name}
                        onChange={handleContactInputChange}
                        placeholder="Your name"
                        className="w-full bg-white border-zinc-300"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-zinc-900 mb-2">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={contactForm.email}
                        onChange={handleContactInputChange}
                        placeholder="you@fund.com"
                        required
                        className="w-full bg-white border-zinc-300"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-900 mb-2">
                      Company / Fund
                    </label>
                    <Input
                      type="text"
                      name="company"
                      value={contactForm.company}
                      onChange={handleContactInputChange}
                      placeholder="Your firm"
                      className="w-full bg-white border-zinc-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-900 mb-2">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <Textarea
                      name="description"
                      value={contactForm.description}
                      onChange={handleContactInputChange}
                      placeholder="How can we help you?"
                      required
                      rows={4}
                      className="w-full bg-white border-zinc-300"
                    />
                  </div>

                  <div className="text-center pt-4">
                    <Button
                      type="submit"
                      disabled={isSubmittingContact}
                      className="w-full py-4 text-lg bg-zinc-900 hover:bg-zinc-800 text-white shadow-lg"
                    >
                      {isSubmittingContact ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Sending...
                        </div>
                      ) : (
                        <div className="flex items-center justify-center">
                          <Send className="w-5 h-5 mr-2" />
                          Send Message
                        </div>
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </FadeIn>

          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
